import React, { useEffect, useMemo, useState } from "react";
import { ChevronDown, History, FileCode, Calculator } from "lucide-react";
import api from "../lib/api";
import { DeliverableViewer } from "./StagePanel";

/**
 * Compact deliverables picker for stage workspaces.
 *
 * Replaces the old 260px sidebar with:
 *   • A dropdown filter showing every deliverable in this stage
 *     (by code · v{n} — name).
 *   • A version picker that lets the user step back to any prior
 *     revision of the selected deliverable (read-only).
 *   • A full-width viewer area for the deliverable content.
 */
export default function DeliverablesPicker({ deliverables, openDeliv, setOpenDeliv, projectId, stageCode }) {
  // The user's selected version. Defaults to the latest (which IS what
  // `openDeliv` already represents until the picker overrides it).
  const [overrideDeliv, setOverrideDeliv] = useState(null);
  const [versions, setVersions] = useState([]);
  const [loadingVersions, setLoadingVersions] = useState(false);

  const current = overrideDeliv || openDeliv;
  const currentCode = current?.code;

  // Reset override + reload versions whenever the user picks a different
  // deliverable from the dropdown.
  useEffect(() => {
    setOverrideDeliv(null);
    if (!currentCode || !projectId) { setVersions([]); return; }
    let alive = true;
    setLoadingVersions(true);
    api.get(`/projects/${projectId}/deliverables/by-code/${encodeURIComponent(currentCode)}/versions`)
      .then((r) => { if (alive) setVersions(Array.isArray(r.data) ? r.data : (r.data?.versions || [])); })
      .catch(() => alive && setVersions([]))
      .finally(() => alive && setLoadingVersions(false));
    return () => { alive = false; };
  }, [currentCode, projectId]);

  const sortedVersions = useMemo(
    () => [...(versions || [])].sort((a, b) => (b.version || 0) - (a.version || 0)),
    [versions]
  );
  const selectedVersionId = current?.id;
  const isViewingPrior = current && openDeliv && current.id !== openDeliv.id;

  return (
    <div data-testid={`deliverables-picker-${stageCode}`}>
      {/* Toolbar — code dropdown + version picker */}
      <div style={{
        display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10,
        padding: "8px 12px", marginBottom: 10,
        background: "var(--pf-surface-2)", border: "1px solid var(--pf-border)", borderRadius: 4,
      }}>
        <span className="pf-overline" style={{ color: "var(--pf-text-muted)" }}>DOCUMENT</span>
        <div style={{ position: "relative", flex: "1 1 360px", maxWidth: 560 }}>
          <select
            data-testid="deliv-code-filter"
            value={openDeliv?.code || ""}
            onChange={(e) => {
              const next = deliverables.find((d) => d.code === e.target.value);
              if (next) setOpenDeliv(next);
            }}
            className="pf-mono"
            style={{
              width: "100%", padding: "6px 28px 6px 10px",
              background: "var(--pf-surface)", color: "var(--pf-text)",
              border: "1px solid var(--pf-border)", borderRadius: 3,
              fontSize: 12, appearance: "none", cursor: "pointer",
            }}
          >
            {deliverables.map((d) => (
              <option key={d.id} value={d.code}>
                {d.code} · v{d.version} — {d.name}
              </option>
            ))}
          </select>
          <ChevronDown size={13} style={{
            position: "absolute", right: 9, top: "50%", transform: "translateY(-50%)",
            color: "var(--pf-text-muted)", pointerEvents: "none",
          }} />
        </div>

        {sortedVersions.length > 1 && (
          <div data-testid="deliv-version-picker" style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span className="pf-overline" style={{ color: "var(--pf-text-muted)", display: "flex", alignItems: "center", gap: 4 }}>
              <History size={11} /> VERSION
            </span>
            <select
              data-testid="deliv-version-select"
              value={selectedVersionId}
              onChange={(e) => {
                const v = sortedVersions.find((x) => x.id === e.target.value);
                if (v) setOverrideDeliv(v);
              }}
              className="pf-mono"
              style={{
                padding: "5px 8px",
                background: "var(--pf-surface)", color: "var(--pf-text)",
                border: "1px solid var(--pf-border)", borderRadius: 3,
                fontSize: 11.5, cursor: "pointer",
              }}
            >
              {sortedVersions.map((v) => (
                <option key={v.id} value={v.id}>
                  v{v.version} {v.is_latest ? "(latest)" : ""} · {(v.created_at || "").slice(0, 10)}
                </option>
              ))}
            </select>
            {isViewingPrior && (
              <button
                type="button"
                data-testid="deliv-version-latest"
                onClick={() => setOverrideDeliv(null)}
                style={{
                  fontSize: 11, padding: "4px 8px", borderRadius: 3,
                  background: "rgba(34,197,94,0.10)", border: "1px solid #22c55e",
                  color: "#86efac", cursor: "pointer",
                }}
              >
                Back to latest
              </button>
            )}
          </div>
        )}
        {loadingVersions && (
          <span style={{ fontSize: 10, color: "var(--pf-text-muted)" }}>loading versions…</span>
        )}

        <div style={{ flex: 1 }} />

        {/* Format/precomputed badges */}
        {current?.format === "svg" && <Badge color="#FFB000" label="SVG" Icon={FileCode} />}
        {current?.precomputed && <Badge color="#22c55e" label="NUM" Icon={Calculator} />}
      </div>

      {isViewingPrior && (
        <div data-testid="deliv-prior-banner" style={{
          padding: "6px 12px", marginBottom: 10, fontSize: 11, borderRadius: 3,
          background: "rgba(234,179,8,0.10)", border: "1px solid #eab308", color: "#fde68a",
        }}>
          Viewing v{current.version} (read-only). The latest is v{openDeliv?.version}. Click <b>Back to latest</b> to resume.
        </div>
      )}

      {/* Full-width viewer */}
      <div className="pf-card" data-testid="deliv-viewer-wrap" style={{
        padding: 18, minHeight: 360, overflow: "auto",
      }}>
        {current ? (
          <DeliverableViewer d={current} projectId={projectId} />
        ) : (
          <div style={{ color: "var(--pf-text-muted)" }}>Pick a deliverable above.</div>
        )}
      </div>
    </div>
  );
}

function Badge({ color, label, Icon }) {
  return (
    <span style={{
      fontSize: 9, padding: "2px 6px", borderRadius: 2,
      border: `1px solid ${color}`, color, fontFamily: "'JetBrains Mono', monospace",
      display: "inline-flex", alignItems: "center", gap: 4,
    }}>
      {Icon && <Icon size={10} />} {label}
    </span>
  );
}
